import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// ── Make Change to Existing Job (Ryan, Oct 22 2026) ────────────────────────────
// Opened from the award window. A bid priced on its own that belongs on a job we
// already have: pick the job, then Work Order (the next 2633-WO###, this bid as its
// quote) or Change Order (the next CO on the job, Unit Price, approved, the bid's
// lines and prices). The app suggests which: a job with work orders takes a WO.

const GREEN = '#16a34a';
const btn = { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 };

export default function MoveBidToJob({ bid, onBack, onClose }) {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState('');
  const [pick, setPick] = useState(null);
  const [as, setAs] = useState('co');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { apiFetch(`/bids/${bid.id}/move-targets`).then(j => setJobs(Array.isArray(j) ? j : [])).catch(e => setErr(e?.error || 'Could not load the jobs.')); }, [bid.id]);
  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (jobs || []).filter(j => words.every(w => `${j.project_number} ${j.name} ${j.client_name || ''}`.toLowerCase().includes(w))).slice(0, 60);
  }, [jobs, q]);
  function choose(j) { setPick(j); setAs(j.suggest); setErr(''); }
  async function go() {
    if (!pick || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(`/bids/${bid.id}/move-to-job`, { method: 'POST', body: JSON.stringify({ project_id: pick.id, as }) });
      window.location.href = r.path;
    } catch (e) { setErr(e?.error || 'Could not move the bid.'); setBusy(false); }
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Make Change to Existing Job</div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3 }}>The GC wants this on a job we already have — pick the job, then Work Order or Change Order.</div>
      </div>
      {!pick ? (
        <>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search jobs — number, name or customer (e.g. 2633)"
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 14 }} />
          <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {jobs === null && !err && <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading jobs…</div>}
            {shown.map(j => (
              <button key={j.id} type="button" onClick={() => choose(j)}
                style={{ textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 800, color: '#111827', minWidth: 54 }}>{j.project_number}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name}{j.client_name ? <span style={{ color: '#9ca3af' }}> · {j.client_name}</span> : null}</span>
                <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{j.work_orders ? `${j.work_orders} WO${j.work_orders === 1 ? '' : 's'}` : ''}{j.work_orders && j.change_orders ? ' · ' : ''}{j.change_orders ? `${j.change_orders} CO${j.change_orders === 1 ? '' : 's'}` : ''}</span>
              </button>
            ))}
            {jobs && !shown.length && <div style={{ fontSize: 13, color: '#9ca3af' }}>No active job matches.</div>}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{pick.project_number} · {pick.name}</div>
              {pick.client_name && <div style={{ fontSize: 12, color: '#6b7280' }}>{pick.client_name}</div>}
            </div>
            <button type="button" onClick={() => setPick(null)} style={{ ...btn, padding: '5px 10px', fontSize: 12 }}>Change job</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { k: 'wo', icon: '🔧', label: 'Work Order', sub: `Becomes ${pick.next_wo}`, desc: 'Its own work order under the job — this bid is its quote.' },
              { k: 'co', icon: '📝', label: 'Change Order', sub: `Becomes ${pick.next_co}`, desc: 'An approved Unit Price change order with this bid\'s lines and prices.' },
            ].map(o => (
              <button key={o.k} type="button" onClick={() => setAs(o.k)}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer', background: as === o.k ? '#f0fdf4' : '#fff', border: `2px solid ${as === o.k ? GREEN : '#e5e7eb'}` }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{o.icon} {o.label}{pick.suggest === o.k && <span style={{ fontSize: 10.5, fontWeight: 700, color: GREEN, marginLeft: 6 }}>suggested</span>}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: GREEN, margin: '3px 0' }}>{o.sub}</div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45 }}>{o.desc}</div>
              </button>
            ))}
          </div>
        </>
      )}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onBack} style={btn}>‹ Back</button>
        <button type="button" onClick={onClose} style={btn}>Cancel</button>
        <button type="button" onClick={go} disabled={!pick || busy}
          style={{ ...btn, border: 'none', background: pick ? GREEN : '#bbf7d0', color: '#fff', cursor: pick ? 'pointer' : 'default' }}>
          {busy ? 'Moving…' : pick ? `Make it ${as === 'wo' ? pick.next_wo : pick.next_co + ' on ' + pick.project_number} →` : 'Pick a job'}
        </button>
      </div>
    </div>
  );
}
